import { BasicVariableElement } from "@src/lib/liquidx/BasicVariableElement";
import { OperationContext } from "../OperationContext";
import { Operation } from "./Operation";
import { ContentControlInsertionHelper } from "./ContentControlInsertionHelper";
import { InsertLocation } from "@src/lib/liquidx";
import { ContentControlUpdate } from "./ContentControlUpdate";

export interface SearchMatch {
  range: Word.Range;
  code: string;
}

/**
 * Searches for text patterns in a document and replaces them with content controls.
 * This operation finds text matches using surrounding context and transforms them
 * into variable elements with specified code properties. This class is specific to the AI coding but
 * could be made more generic and used for other operations.
 */
export class DocumentSearchAndReplace implements Operation<void> {
  private static readonly searchOptions = {
    matchCase: false,
    matchWholeWord: false,
    ignorePunct: true,
    ignoreSpace: true,
    matchWildcards: false,
  };

  constructor(
    private readonly replacements: Array<{
      originalText: string;
      surroundingText: string;
      code: string;
    }>,
    private signal?: AbortSignal,
  ) {}

  merge(): Operation<void> | undefined {
    return undefined;
  }

  async commit(context: OperationContext): Promise<void> {
    if (this.signal?.aborted) return;

    let allMatches: SearchMatch[] = [];
    for (const replacement of this.replacements) {
      if (this.signal?.aborted) return;

      const surroundingText = replacement.surroundingText.replace(/\n/g, "\r");
      const surroundingResults = context.document.body.search(surroundingText, DocumentSearchAndReplace.searchOptions);
      surroundingResults.load("items");
      await context.sync();

      if (surroundingResults.items.length === 0) {
        console.error(`No surrounding results found for ${replacement.surroundingText}`);
        continue;
      }

      for (const surroundingItem of surroundingResults.items) {
        const contextualSearchResults = surroundingItem.search(
          replacement.originalText,
          DocumentSearchAndReplace.searchOptions,
        );
        contextualSearchResults.load("items");
        await context.sync();

        for (const item of contextualSearchResults.items) {
          allMatches.push({
            range: item,
            code: replacement.code,
          });
        }
      }
    }

    for (const match of allMatches) {
      if (this.signal?.aborted) return;

      match.range.select();
      await context.sync();

      if (this.signal?.aborted) return;

      const { control: contentControl } = await ContentControlInsertionHelper.insertContentControl(
        context.document.context,
        InsertLocation.SELECTION,
      );

      const element = new BasicVariableElement();
      element.ui.property = match.code;
      element.render.code = element.buildCode();
      element.ui.label = element.buildLabel();
      const update = new ContentControlUpdate(element.params);
      await update.updateContentControl(context, contentControl);
      if (this.signal?.aborted) return;
      await context.sync();
    }
  }
}
